// src/pages/shelbula/workspaces/DirectActionModal.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader, Zap, X, Eye, Search, AlertTriangle, FolderGit2, ChevronDown, Github } from 'lucide-react';
import { authManager } from '@/managers/AuthManager';
import { botManager } from '@/managers/BotManager';
import { useTheme } from '@/managers/ThemeProvider';
import { apiManager } from './apiManager';

const PlatformChoiceChip = ({ platform, isSelected, onClick }) => {
    const { isDarkTheme } = useTheme();

    const getChipStyle = () => {
        const p = String(platform).toUpperCase();
        let bgColor = '', textColor = '';
        switch (p) {
            case 'OPENAI':
                bgColor = isDarkTheme ? 'bg-emerald-500/10' : 'bg-emerald-500/20';
                textColor = isDarkTheme ? 'text-emerald-400' : 'text-emerald-800';
                break;
            case 'CLAUDE':
                bgColor = isDarkTheme ? 'bg-[#F6A192]/10' : 'bg-[#F6A192]/20';
                textColor = isDarkTheme ? 'text-[#F6A192]' : 'text-[#8B5C52]';
                break;
            case 'GEMINI':
                bgColor = isDarkTheme ? 'bg-violet-400/10' : 'bg-violet-400/20';
                textColor = isDarkTheme ? 'text-violet-400' : 'text-violet-800';
                break;
            case 'MISTRAL':
                bgColor = isDarkTheme ? 'bg-blue-400/10' : 'bg-blue-400/20';
                textColor = isDarkTheme ? 'text-blue-400' : 'text-blue-800';
                break;
            default:
                bgColor = 'bg-surface-dark';
                textColor = 'text-on-surface-dim';
                break;
        }
        return `${bgColor} ${textColor}`;
    };

    return (
        <button
            type="button"
            onClick={onClick}
            title={`Use ${platform}`}
            className={`px-1.5 py-1 rounded-md text-xs font-bold border-2 transition-all duration-150 ${getChipStyle()} ${isSelected ? 'border-primary' : 'border-transparent'}`}
        >
            {platform.charAt(0)}
        </button>
    );
};


const DirectActionModal = ({ isOpen, onClose, activeWorkspace, allWorkspaces = [], onPhaseInitiated = () => {}, onPhaseError = () => {}, atLimit = false }) => {
    const [prompt, setPrompt] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [internalError, setInternalError] = useState(null);
    const [requiresApproval, setRequiresApproval] = useState(true);
    const [isAnalysisOnly, setIsAnalysisOnly] = useState(false);
    const [intendedOutcome, setIntendedOutcome] = useState('CODE_WRITING_EDITING');

    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('new');
    const [taskPlatform, setTaskPlatform] = useState(null);
    const [availablePlatforms, setAvailablePlatforms] = useState([]);
    const [authState] = useState(authManager.getState());

    useEffect(() => {
        if (isOpen) {
            setPrompt('');
            setIsSubmitting(false);
            setInternalError(null);
            setRequiresApproval(true);
            setIsAnalysisOnly(false);
            setIntendedOutcome('CODE_WRITING_EDITING');

            const currentBotContext = botManager.getCurrentBotContext();
            setTaskPlatform(currentBotContext.serverPlatform);
            setAvailablePlatforms(['OPENAI', 'CLAUDE', 'GEMINI', 'MISTRAL']);

            const hasExistingWorkspaces = allWorkspaces.length > 0;
            if (activeWorkspace) {
                setSelectedWorkspaceId(activeWorkspace.id);
            } else if (!atLimit) {
                setSelectedWorkspaceId('new');
            } else if (hasExistingWorkspaces) {
                setSelectedWorkspaceId(allWorkspaces[0].id);
            } else {
                setSelectedWorkspaceId('new');
            }
        }
    }, [isOpen, activeWorkspace, atLimit, allWorkspaces]);

    useEffect(() => {
        // This logic remains untouched, as requested. It handles the 'Analysis' checkbox.
        if (isAnalysisOnly) {
            setRequiresApproval(false);
            if (selectedWorkspaceId === 'new' && allWorkspaces.length > 0) {
                setSelectedWorkspaceId(allWorkspaces[0].id);
            }
        } else {
            // Restore default for constructive tasks, unless the dropdown dictates otherwise.
            if (intendedOutcome !== 'DOCUMENTATION') {
                 setRequiresApproval(true);
            }
        }
        // Logic for 'DOCUMENTATION' intent from dropdown
        if (intendedOutcome === 'DOCUMENTATION') {
            setRequiresApproval(false); // Documentation does not need a plan review.
             if (selectedWorkspaceId === 'new' && allWorkspaces.length > 0) {
                setSelectedWorkspaceId(allWorkspaces[0].id);
            }
        }
    }, [isAnalysisOnly, intendedOutcome, allWorkspaces, selectedWorkspaceId]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        if (isOpen) window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    const outcomeIntentOptions = [
        { value: 'CODE_WRITING_EDITING', label: 'Code Writing / Editing' },
        { value: 'DOCUMENTATION', label: 'Create Documentation' }, // ADDED AS REQUESTED
        { value: 'PROSE_WRITING_EDITING', label: 'Prose Writing / Editing' },
        { value: 'TEST_AND_REPAIR', label: 'Test & Repair' },
        { value: 'LOG_AND_DEBUG', label: 'Log Analysis & Debugging' },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isTextOptionalTask = isAnalysisOnly || intendedOutcome === 'DOCUMENTATION';
        if ((!prompt.trim() && !isTextOptionalTask) || isSubmitting || !authState.user) {
             if (!prompt.trim() && !isTextOptionalTask) setInternalError("Please provide a task objective.");
             return;
        }

        setIsSubmitting(true);
        setInternalError(null);
        try {
            const workspaceIdForApi = selectedWorkspaceId === 'new' ? null : selectedWorkspaceId;
            let result;

            // Use the new, explicit API call for documentation.
            if (intendedOutcome === 'DOCUMENTATION') {
                result = await apiManager.initiateDocumentation(workspaceIdForApi, prompt.trim());
            } else {
                const finalOutcomeIntent = isAnalysisOnly ? 'ANALYSIS' : intendedOutcome;
                const task = {
                    taskId: `client-task-${Date.now()}`,
                    taskDescription: (finalOutcomeIntent === 'ANALYSIS' && !prompt.trim()) ? 'Analyze' : prompt.trim(),
                    outcomeIntent: finalOutcomeIntent,
                };
                const autoCommit = !requiresApproval;
                result = await apiManager.delegateTask(workspaceIdForApi, task, autoCommit);
            }

            if (result.success) {
                onPhaseInitiated(result.message);
                onClose();
            } else {
                const errorPayload = result.error || 'An unknown error occurred.';
                const errorMessage = (errorPayload && typeof errorPayload === 'object' && errorPayload.message) ? errorPayload.message : String(errorPayload);
                onPhaseError(errorMessage);
                setInternalError(errorMessage);
            }
        } catch (err) {
            const errorPayload = err.message || err;
            const errorMessage = (errorPayload && typeof errorPayload === 'object' && errorPayload.message) ? errorPayload.message : String(errorPayload);
            onPhaseError(errorMessage);
            setInternalError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isTextOptionalTask = isAnalysisOnly || intendedOutcome === 'DOCUMENTATION';
    const isSubmitDisabled = isSubmitting || (!prompt.trim() && !isTextOptionalTask);
    const canSelectNewWorkspace = !isAnalysisOnly && intendedOutcome !== 'DOCUMENTATION';
    const placeholderText = intendedOutcome === 'DOCUMENTATION' 
        ? "You can leave this blank to document the entire project, or be specific: 'Focus documentation on the auth and API layers.'"
        : isAnalysisOnly 
            ? "You can leave this blank for a general analysis, or be specific: 'Analyze the codebase for potential security vulnerabilities and list them.'" 
            : "Example: 'Refactor the authentication flow to use JWT instead of session cookies...'";


    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute inset-0 z-20 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="relative w-full max-w-xl p-6 bg-surface border border-border-color rounded-lg shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full text-on-surface-dim hover:bg-surface-dark">
                            <X size={18} />
                        </button>

                        <div className="flex items-center gap-3">
                             <div className="w-9 h-9 flex items-center justify-center rounded-md border border-primary/50 bg-surface">
                                <Zap size={18} className="text-primary" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-on-surface">Delegate a New Task</h3>
                                <p className="text-sm text-on-surface-dim mt-0.5">Describe a high-level goal for Evōk to execute.</p>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-on-surface flex items-center gap-2">
                                    <FolderGit2 size={14} />
                                    Target Workspace
                                </label>
                                <div className="relative">
                                    <select
                                        value={selectedWorkspaceId}
                                        onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                                        className="w-full appearance-none p-2 pr-8 bg-background border border-border-color rounded-md text-sm text-on-surface focus:ring-1 focus:ring-primary outline-none"
                                    >
                                        {canSelectNewWorkspace && (
                                            <option value="new" disabled={atLimit}>
                                                {atLimit ? "-- Workspace Limit Reached (Upgrade Plan) --" : "-- Create New Workspace --"}
                                            </option>
                                        )}
                                        {allWorkspaces.map(ws => (
                                            <option key={ws.id} value={ws.id}>{ws.workspaceName} ({ws.id})</option>
                                        ))}
                                         {!canSelectNewWorkspace && allWorkspaces.length === 0 && (
                                            <option value="" disabled>-- No existing workspaces available for this task type --</option>
                                        )}
                                    </select>
                                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-dim pointer-events-none" />
                                </div>
                            </div>
                            <textarea
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder={placeholderText}
                                className="w-full p-3 bg-background-dark border border-border-color rounded-md text-sm text-on-surface focus:ring-1 focus:ring-primary outline-none resize-none"
                                rows={4} autoFocus disabled={isSubmitting}
                            />

                             {internalError && (
                                 <div className="flex items-start gap-2.5 text-sm p-3 rounded-lg border bg-red-500/10 border-red-500/30 text-red-400">
                                    <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                                    <span>{internalError}</span>
                                </div>
                             )}

                            <div className="space-y-2">
                                {/* Top Row: Checkboxes and Platform Chips */}
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-6">
                                        <button type="button" onClick={() => setRequiresApproval(!requiresApproval)} disabled={isAnalysisOnly || intendedOutcome === 'DOCUMENTATION'} className="flex items-center gap-2 text-sm text-on-surface-dim hover:text-on-surface disabled:opacity-50 disabled:cursor-not-allowed">
                                            <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center transition-colors ${requiresApproval ? 'bg-primary border-primary' : 'border-on-surface-dim/50'}`}>
                                                {requiresApproval && <Eye size={10} className="text-white" />}
                                            </div>
                                            <span className="font-medium">Review Plan First</span>
                                        </button>
                                        <button type="button" onClick={() => setIsAnalysisOnly(!isAnalysisOnly)} disabled={selectedWorkspaceId === 'new' || intendedOutcome === 'DOCUMENTATION'} title={selectedWorkspaceId === 'new' ? 'Analysis requires an existing workspace' : ''} className="flex items-center gap-2 text-sm text-on-surface-dim hover:text-on-surface disabled:opacity-50 disabled:cursor-not-allowed">
                                            <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center transition-colors ${isAnalysisOnly ? 'bg-primary border-primary' : 'border-on-surface-dim/50'}`}>
                                                {isAnalysisOnly && <Search size={8} className="text-white" />}
                                            </div>
                                            <span className="font-medium">Read-Only Analysis</span>
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {availablePlatforms.map(p => (
                                            <PlatformChoiceChip key={p} platform={p} isSelected={taskPlatform === p} onClick={() => setTaskPlatform(p)} />
                                        ))}
                                    </div>
                                </div>

                                {/* Bottom Row: Dropdown and Delegate Button */}
                                <div className="flex justify-between items-end pt-2">
                                    <div className="flex flex-col gap-1">
                                        <label className="text-sm font-medium text-on-surface">Intended Outcome</label>
                                        <div className="relative">
                                            <select
                                                value={intendedOutcome}
                                                onChange={(e) => setIntendedOutcome(e.target.value)}
                                                disabled={isAnalysisOnly || isSubmitting}
                                                className="w-auto appearance-none p-2.5 pr-8 bg-background border border-border-color rounded-md text-sm text-on-surface focus:ring-1 focus:ring-primary outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {outcomeIntentOptions.map(option => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                            <ChevronDown size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-dim pointer-events-none" />
                                        </div>
                                    </div>
                                    <button type="submit" disabled={isSubmitDisabled} className="px-5 py-2.5 text-sm font-semibold rounded-md bg-primary text-white hover:bg-primary-dark disabled:bg-primary/50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap">
                                        {isSubmitting ? <Loader size={16} className="animate-spin" /> : <Zap size={16} />}
                                        Delegate Task
                                    </button>
                                </div>
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default DirectActionModal;